/* eslint-disable max-lines-per-function */
import {
  Box,
  Button,
  Flex,
  Image,
  Text
} from "@chakra-ui/react"
import { useEffect, useState } from "react"
import cert from "../assets/images/accrediation_certificate.jpeg"
import bg from "../assets/images/quality-accrediation.png"
import C1457File from "../assets/pdf/W_LPCB_Issue2_C1457_SFS-Certificate.pdf"
import QualityAssurancePolicyFile from "../assets/pdf/sf-qal-pol-01-quality-assurance-policy-v1.pdf"
import { breadCrumbFirstLevel, breadCrumbReportsAndResourcesSecondLevel } from "../common/commonConstants"
import { buildDocumentUrl } from "../common/documentUrl"
import { SubPage, SubPageContent } from "../components/SubPage"
import { RHeading3PT } from "../components/commons/RHeading3PT"
import { BreadCrumb } from "../components/commons/breadcrumb"
import { StaticPageHeader } from "../components/commons/static-page-header"
import { PDFIcon } from "../components/productInfo/product-info.main"
import { Colors } from "../theme/colors"
import { useGetQualityCertificatesQuery } from "../type-and-hooks"

interface CertificateEntry {
  url: string
  fileName?: string | null
}

export const Accrediation = (): JSX.Element => {
  const [certificateUrls, setCertificateUrls] = useState<Record<string, CertificateEntry>>({})
  const { data: certificatesData } = useGetQualityCertificatesQuery()

  useEffect(() => {
    if (certificatesData?.getQualityCertificates) {
      const urlMap: Record<string, CertificateEntry> = {}
      certificatesData.getQualityCertificates.forEach(cert => {
        if (cert?.certificate_name && cert?.certificate_url) {
          urlMap[cert.certificate_name] = { url: cert.certificate_url, fileName: cert.file_name }
        }
      })
      setCertificateUrls(urlMap)
    }
  }, [certificatesData])

  const handlePreview = (url: string, fileName?: string | null): void => {
    window.open(buildDocumentUrl(fileName, url) ?? url, "_blank", "noopener,noreferrer")
  }

  const DocRow = ({ doc, label }: { doc: CertificateEntry | string | undefined; label: string }): JSX.Element | null => {
    if (!doc) return null
    const url = typeof doc === "string" ? doc : doc.url
    const fileName = typeof doc === "string" ? undefined : doc.fileName
    return (
      <Flex
        align="center"
        py="8px"
        borderBottom="1px solid"
        borderColor={Colors.Secondary.c2_300}
        _last={{ borderBottom: "none" }}
        gap="8px"
      >
        <Button
          variant="linkButton"
          textDecoration="none"
          fontWeight="400"
          fontSize="16px"
          lineHeight="18px"
          textAlign="left"
          flex="1"
          justifyContent="flex-start"
          whiteSpace="normal"
          h="auto"
          minH="32px"
          display="flex"
          alignItems="center"
          gap="8px"
          onClick={() => { handlePreview(url, fileName) }}
        >
          <PDFIcon />
          {label}
        </Button>
      </Flex>
    )
  }

  return (
    <>
      <SubPage>
        <StaticPageHeader title="Quality Certification" bg={bg} />
        <BreadCrumb
          options={{
            ...breadCrumbFirstLevel,
            ...breadCrumbReportsAndResourcesSecondLevel,
            thirdLevel: { title: "Quality Certification" }
          }}
        />
        <SubPageContent>
          <Flex align={"center"} flexDir={"column"} w="100%">
            <RHeading3PT
              text="We have received ISO 9001 Management Systems Certification"
              props={{
                w: ["auto", "auto", "450px"],
                textAlign: "center",
                py: ["4px", "4px", "32px"],
                pb: ["9px", "9px", "10px"]
              }}
            />
            <Image w="290px" mt={"14px"} src={cert} />
            {certificateUrls.CERT1_IMG && (
              <Image w={["332px", "332px", "611px"]} mt={["2", "2", "18px"]} src={certificateUrls.CERT1_IMG.url} />
            )}
            <Box w={["100%", "100%", "580px"]} mt="16px">
              <DocRow doc={certificateUrls.C1457 ?? (C1457File as string)} label="ISO 9001 Management Systems Certification" />
            </Box>

            <Box gap={"16px"} maxW={["100%", "100%", "100%", "580px"]} w={["100%", "100%", "580px"]}>
              <Flex align={"center"} flexDir={"column"} w="100%">
                <RHeading3PT
                  text="Third Party Product Certification"
                  props={{
                    w: ["auto", "auto", "400px"],
                    textAlign: "center",
                    py: ["4px", "4px", "32px"],
                    pb: ["9px", "9px", "10px"]
                  }}
                />
              </Flex>
              <Text variant="Body1Regular" mt={["22px", "22px", "16px"]} textAlign={"left"}>
                We have third party product certification for products we manufacture and export to the United Kingdom.
              </Text>
              <Text variant="Body1Regular" mt={["12px", "12px", "14px"]} textAlign={"left"}>
                Our factory processes for this certification are applied across all of the products we manufacture including those for the Australian
                market. As a part of that certification process, our processes and facilities are audited annually.
              </Text>
              <Text variant="Body1Regular" mt={["12px", "12px", "16px"]} textAlign={"left"}>
                We are also subject to product surveillance testing to ensure that our collars perform as they did when they were originally tested.
              </Text>
              <Box mt="16px">
                <DocRow doc={certificateUrls.C1833a} label="C1833A Certification Certificate" />
                <DocRow doc={QualityAssurancePolicyFile as string} label="Quality Assurance Policy" />
              </Box>
            </Box>

            {certificateUrls.CERT2_IMG && (
              <Box>
                <Image w={["332px", "332px", "611px"]} mt={["2", "2", "18px"]} src={certificateUrls.CERT2_IMG.url} />
              </Box>
            )}
          </Flex>
        </SubPageContent>
      </SubPage>
    </>
  )
}
